// src/pages/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Button from '../components/customButtons/Button';
import { useAuth } from '../context/AuthContext';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { resetPassword } = useAuth();

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        feedback: []
    });

    useEffect(() => {
        const tokenFromUrl = searchParams.get('token');
        if (!tokenFromUrl) {
            setError('Invalid or missing reset token.');
        }
        setToken(tokenFromUrl);
    }, [searchParams]);

    // Enhanced password validation with strength checking
    const checkPasswordStrength = (password) => {
        const feedback = [];
        let score = 0;

        // Length check
        if (password.length >= 8) {
            score += 1;
        } else {
            feedback.push('At least 8 characters');
        }

        // Uppercase check
        if (/[A-Z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One uppercase letter');
        }

        // Lowercase check
        if (/[a-z]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One lowercase letter');
        }

        // Number check
        if (/\d/.test(password)) {
            score += 1;
        } else {
            feedback.push('One number');
        }

        // Special character check
        if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
            score += 1;
        } else {
            feedback.push('One special character');
        }

        return { score, feedback };
    };

    const validatePassword = (password) => {
        if (!password) return 'Password is required';
        if (password.length < 8) return 'Password must be at least 8 characters';
        if (password.length > 128) return 'Password too long';

        const strength = checkPasswordStrength(password);
        if (strength.score < 3) {
            return 'Password is too weak';
        }

        return '';
    };

    // Check if password is the same as common passwords or patterns
    const isCommonPassword = (password) => {
        const commonPasswords = [
            'password', '12345678', 'qwerty', 'letmein', 'welcome',
            'admin', 'password1', '123456789', '1234567', '123123'
        ];
        return commonPasswords.includes(password.toLowerCase());
    };

    const isSequential = (password) => {
        const sequences = [
            '12345678', '23456789', '34567890', 'abcdefgh', 'bcdefghi',
            'qwertyui', 'asdfghjk', 'zxcvbnm'
        ];
        return sequences.some(seq => password.toLowerCase().includes(seq));
    };

    const validateForm = () => {
        const newErrors = {};

        // Password validation
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            newErrors.password = passwordError;
        } else {
            // Additional security checks
            if (isCommonPassword(formData.password)) {
                newErrors.password = 'This password is too common. Please choose a more unique password.';
            } else if (isSequential(formData.password)) {
                newErrors.password = 'Sequential patterns are not allowed for security reasons.';
            } else if (formData.password.includes(' ')) {
                newErrors.password = 'Password cannot contain spaces.';
            }
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        // Remove empty error messages
        Object.keys(newErrors).forEach(key => {
            if (!newErrors[key]) delete newErrors[key];
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Update password strength in real-time
        if (name === 'password') {
            const strength = checkPasswordStrength(value);
            setPasswordStrength(strength);
        }

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Clear confirm password error when passwords match
        if (name === 'confirmPassword' && errors.confirmPassword && value === formData.password) {
            setErrors(prev => ({
                ...prev,
                confirmPassword: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!token) {
            setError('Invalid reset token');
            return;
        }

        if (!validateForm()) return;

        setIsSubmitting(true);
        setError('');

        try {
            await resetPassword(token, formData.password);
            setMessage('Your password has been reset successfully!');

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            // Handle specific backend errors
            if (err.message.includes('same as current')) {
                setError('You cannot reuse your current password. Please choose a different password.');
            } else if (err.message.includes('recently used')) {
                setError('This password was recently used. Please choose a different password.');
            } else {
                setError(err.message || 'Failed to reset password. The link may have expired.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get password strength color and text
    const getStrengthInfo = () => {
        if (formData.password.length === 0) return { color: 'gray', text: 'Enter a password' };

        switch (passwordStrength.score) {
            case 0:
            case 1:
                return { color: 'red', text: 'Very Weak' };
            case 2:
                return { color: 'orange', text: 'Weak' };
            case 3:
                return { color: 'yellow', text: 'Fair' };
            case 4:
                return { color: 'blue', text: 'Good' };
            case 5:
                return { color: 'green', text: 'Strong' };
            default:
                return { color: 'gray', text: '' };
        }
    };

    const strengthInfo = getStrengthInfo();

    return (
        <div className="h-screen bg-neutral flex flex-col justify-center px-4 sm:px-6 lg:px-8 font-sans">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-2xl font-extrabold text-primary mb-4 font-heading">
                    Set New Password
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 max-w-sm mx-auto font-body">
                    Create a strong, unique password for your account.
                </p>
            </div>

            <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-10 px-6 shadow rounded-2xl sm:px-12 border border-gray-50">
                    {message && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                <span className="text-green-700 font-medium font-body">Success</span>
                            </div>
                            <p className="text-green-600 text-sm mt-1 font-body">{message}</p>
                            <p className="text-green-600 text-sm mt-1 font-body">Redirecting to login page...</p>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 text-red-600 text-center font-medium p-3 rounded-lg bg-red-50 border border-red-100 font-body">
                            {error}
                        </div>
                    )}

                    {!message && (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        required
                                        minLength="8"
                                        maxLength="128"
                                        value={formData.password}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter your new password"
                                        className={`block w-full border ${errors.password ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4 pr-10
                               focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(prev => !prev)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.54 18.54 0 0 1 2.58-3.15M2.37 2.37L21.63 21.63M16.13 16.13a4 4 0 0 1-5.42-5.42M4.72 4.72A10.07 10.07 0 0 1 12 4c7 0 11 8 11 8a18.54 18.54 0 0 1-2.58 3.15" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        )}
                                    </button>
                                </div>

                                {/* Password Strength Meter */}
                                {formData.password && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-gray-500 font-body">
                                                Password strength: <span style={{ color: strengthInfo.color }} className="font-semibold">
                                                    {strengthInfo.text}
                                                </span>
                                            </span>
                                            <span className="text-xs text-gray-500 font-body">
                                                {passwordStrength.score}/5
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${(passwordStrength.score / 5) * 100}%`,
                                                    backgroundColor: strengthInfo.color
                                                }}
                                            ></div>
                                        </div>

                                        {/* Password Requirements */}
                                        {passwordStrength.feedback.length > 0 && (
                                            <div className="mt-2 text-xs text-gray-600 font-body">
                                                <p className="font-medium mb-1">Requirements:</p>
                                                <ul className="list-disc list-inside space-y-1">
                                                    {passwordStrength.feedback.map((req, index) => (
                                                        <li key={index} className="text-red-500">{req}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.password}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        required
                                        value={formData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Confirm your new password"
                                        className={`block w-full border ${errors.confirmPassword ? 'border-red-400' : formData.confirmPassword && formData.password === formData.confirmPassword ? 'border-green-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4 pr-10
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(prev => !prev)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showConfirmPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.54 18.54 0 0 1 2.58-3.15M2.37 2.37L21.63 21.63M16.13 16.13a4 4 0 0 1-5.42-5.42M4.72 4.72A10.07 10.07 0 0 1 12 4c7 0 11 8 11 8a18.54 18.54 0 0 1-2.58 3.15" />
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.confirmPassword}</p>
                                )}
                                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                                    <p className="mt-1 text-sm text-green-600 font-medium font-body">✓ Passwords match</p>
                                )}
                            </div>


                            <div>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || !token || passwordStrength.score < 3}
                                    className="w-full py-3 px-4 text-base rounded-lg font-accent"
                                    variant="primary"
                                >
                                    {isSubmitting ? (
                                        <span className="flex items-center justify-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Resetting...
                                        </span>
                                    ) : 'Reset Password'}
                                </Button>
                            </div>
                        </form>
                    )}

                    <div className="mt-8 text-center">
                        <Link
                            to="/login"
                            className="text-primary hover:text-primary-light font-medium text-sm hover:underline font-body"
                        >
                            Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;